import { useMemo } from 'react';
import { omitBy, isNil } from 'lodash';

import { ensureDefined } from '@e-socna/utils';
import {
  CodingStrategyEnum,
  MissionKindEnum,
  SubMissionActorCaseEnum,
} from '@e-socna/data-layer';
import type { Id } from '@e-socna/data-layer';
import { EMPTY_STRING } from '@e-socna/empty';
import { EMPTY_ID } from '@e-socna/empty-client';

import { useIdFactory } from '../../../hooks/useIdFactory.hook';
import { useReferencesContext } from '../../../contexts/ReferencesContext/hooks/useReferencesContext.hook';
import { useAuthenticationContext } from '../../../contexts/AuthenticationContext/hooks/useAuthenticationContext.hook';
import { useIdParam } from '../../../utils/use-id-param.util';

import { filterMissionWorkflowsOfKind } from '../utils/mission-workflows-of-kind.util';
import type { MissionForm_Mission } from '../fragments/mission.fragment';

export const useInitialValuesFactory = (
  pInitialValues: Readonly<MissionForm_Mission> | null | undefined,
  kind: MissionKindEnum,
  compositeMissionId?: Id | null,
) => {
  const { generateId } = useIdFactory();

  const folderId = useIdParam('folderId');

  const referencesContext = useReferencesContext();

  const { defaultMissionWorkflow } = ensureDefined({
    defaultMissionWorkflow: referencesContext.defaultMissionWorkflow,
  });

  const { codingStrategiesCodeMap, missionWorkflows } = referencesContext;

  const workflowsOfKind = filterMissionWorkflowsOfKind(missionWorkflows, kind);
  // A sub-mission is billed by its collaboration by default.
  const defaultWorkflowId =
    kind === MissionKindEnum.Standard
      ? defaultMissionWorkflow._id
      : ((
          workflowsOfKind.find(
            (workflow) =>
              workflow.settings.subMissionActorCase ===
              SubMissionActorCaseEnum.SameEmitterAndReceiver,
          ) ?? workflowsOfKind[0]
        )?._id ?? EMPTY_ID);

  const now = useMemo(() => new Date(), []);

  const { authenticationData } = useAuthenticationContext();
  const businessCreatorId = authenticationData?.person._id ?? EMPTY_ID;

  const initialValues: MissionForm_Mission = useMemo(
    () => ({
      __typename: 'Mission' as const,
      _id: generateId(),
      folderId,
      kind,
      compositeMissionId: compositeMissionId ?? null,
      code: EMPTY_STRING,
      categoryParentId: EMPTY_ID,
      categoryLeaf: EMPTY_STRING,
      memo: EMPTY_STRING,
      internalMemo: EMPTY_STRING,
      color: EMPTY_STRING,
      businessCreatorId,
      businessCreationDate: now.toISOString(),
      workflowId: defaultWorkflowId,
      isConfidential: false,
      ...omitBy(pInitialValues, isNil),
      codingConfig: {
        __typename: CodingStrategyEnum.CodingConfigManual as const,
        _id: generateId(),
        kind: CodingStrategyEnum.CodingConfigManual,
        strategyId: codingStrategiesCodeMap.CodingConfigManual._id,
        code: EMPTY_STRING,
        ...omitBy(pInitialValues?.codingConfig, isNil),
      },
    }),
    [
      businessCreatorId,
      codingStrategiesCodeMap.CodingConfigManual._id,
      compositeMissionId,
      defaultWorkflowId,
      folderId,
      generateId,
      kind,
      now,
      pInitialValues,
    ],
  );

  return { initialValues };
};
